import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import StockTable, { StockTableSkeleton } from "../../interface/shared/stocktable";
import { useProductDetail, useProductExpiryWarnings, ExpiryWarningLevels } from "../../api/products";
import { Product } from "../../shared/types";

export default function ProductDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { product, stock, errors, loading, fetchProduct } = useProductDetail(id);

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/products")}
                    >
                        Back
                    </Button>
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchProduct}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : product && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_ProductSummary product={product} stockCount={stock.length} />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Stock ({stock.length})
                            </Typography>

                            <StockTable stock={stock} emptyMessage="No stock found for this product." />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Expiry Warning Levels
                            </Typography>

                            <_ExpiryWarnings productId={id!} />
                        </StaggerItem>
                    </StaggerContainer>
                )}
            </Box>
        </DashboardLayout>
    );
}

function _ProductSummary({ product, stockCount }: { product: Product; stockCount: number }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {product.code} {product.main_code.length > 0 && product.main_code !== product.code ? `/ ${product.main_code}` : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {product.name}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Chip
                            label={product.active ? "Active" : "Inactive"}
                            size="small"
                            color={product.active ? "success" : "default"}
                        />
                        <Chip label={`${stockCount} stock entries`} size="small" />
                    </Stack>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Barcode</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.barcode || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Size</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.size || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Pack</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.pack || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Weight</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.weight} kg</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="body1" fontWeight={500}>{product.status}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _ExpiryWarnings({ productId }: { productId: string }) {
    const { levels, loading, saving, error, success, setSuccess, saveLevels } = useProductExpiryWarnings(productId);
    const [draft, setDraft] = useState<ExpiryWarningLevels>({ notice: null, warning: null, critical: null });
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setDraft(levels);
        setDirty(false);
    }, [levels]);

    function handleChange(field: keyof ExpiryWarningLevels, value: string) {
        const parsed = value === "" ? null : parseInt(value, 10);
        if (value !== "" && isNaN(parsed as number)) return;
        setDraft((prev) => ({ ...prev, [field]: parsed }));
        setDirty(true);
    }

    if (loading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 4 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="rounded" height={40} sx={{ mt: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Notice (days)</Typography>
                            <TextField
                                placeholder="Notice (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.notice ?? ""}
                                onChange={(e) => handleChange("notice", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Early warning threshold"
                                color="info"
                                focused={draft.notice != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Warning (days)</Typography>
                            <TextField
                                placeholder="Warning (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.warning ?? ""}
                                onChange={(e) => handleChange("warning", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Approaching expiry threshold"
                                color="warning"
                                focused={draft.warning != null}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary">Critical (days)</Typography>
                            <TextField
                                placeholder="Critical (days)"
                                type="number"
                                size="small"
                                fullWidth
                                value={draft.critical ?? ""}
                                onChange={(e) => handleChange("critical", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                helperText="Urgent expiry threshold"
                                color="error"
                                focused={draft.critical != null}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!dirty || saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                            onClick={() => saveLevels(draft)}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)} variant="filled">
                    Warning levels saved successfully.
                </Alert>
            </Snackbar>
        </>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 2 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={120} height={28} sx={{ mt: 4, mb: 2 }} />
            <StockTableSkeleton />
        </>
    );
}
